import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';

const MENU_CLOSE_DELAY = 450;

const MenuHoverContext = createContext({
  cancelClose: () => { },
  scheduleClose: () => { },
});

const MODULE_TREE = [
  {
    id: 'sales', label: 'Sales', children: [
      { id: 'so-list', label: 'Sales Orders', path: '/sales/orders' },
    ]
  },
  {
    id: 'vendor-risk', label: 'Vendor Risk Management', children: [
      { id: 'vm-risk', label: 'Vendor Risk Dashboard', path: '/vendor-management/risk' },
    ]
  },

  // {
  //   id: 'purchasing',
  //   label: 'Purchasing',
  //   children: [
  //     { id: 'po-list', label: 'Purchase Order', path: '/purchasing/orders' },
  //     { id: 'rework-list', label: 'Rework Orders', path: '/production/rework' },
  //   ]
  // },
  {
    id: 'purchasing', label: 'Purchasing', children: [
      { id: 'po-list', label: 'Purchase Order', path: '/purchasing/orders' },
      { id: 'rework-list', label: 'Rework Orders', path: '/production/rework' },
    ]
  },
  {
    id: 'bp', label: 'Business Partners', children: [
      { id: 'vendor-list', label: 'Vendors', path: '/purchasing/vendors' },
    ]
  },
  {
    id: 'inventory', label: 'Inventory', children: [
      { id: 'inv-items', label: 'Item Master Data', path: '/inventory/items' },
      { id: 'inv-aging', label: 'Inventory Aging Report', path: '/inventory/aging' },
      { id: 'inv-postings', label: 'Inventory Posting List', path: '/inventory/postings' },
      {
        id: 'inv-transactions', label: 'Inventory Transactions', children: [
          { id: 'inv-transfer-req', label: 'Inventory Transfer Request', path: '/inventory/transfer-requests' },
          { id: 'inv-transfers', label: 'Inventory Transfer', path: '/inventory/transfers' },
        ]
      },
      { id: 'inv-transfer-report', label: 'Inventory Transfer Report', path: '/inventory/transfer-report' },
      { id: 'inv-new-procurement', label: 'New Procurement Items Report', path: '/inventory/new-procurement-report' },
    ]
  },
  {
    id: 'resources', label: 'Resources', children: [
      { id: 'res-master', label: 'Resource Master', path: '/resources/master' },
    ]
  },
  {
    id: 'production', label: 'Production', children: [
      { id: 'prod-bom', label: 'Bill of Materials', path: '/production/bom' },
      { id: 'prod-bom-csv', label: 'BOM CSV import', path: '/production/bom-import' },
      { id: 'prod-orders', label: 'Production Order', path: '/production/orders' },
      { id: 'prod-issues', label: 'Issue for Production', path: '/production/issues' },
      { id: 'prod-receipts', label: 'Receipts from Production', path: '/production/receipts' },
    ]
  },
  {
    id: 'mrp', label: 'MRP', children: [
      { id: 'mrp-forecast', label: 'Forecasts', path: '/mrp/forecasts' },
      { id: 'mrp-wizard', label: 'MRP Wizard', path: '/mrp/wizard' },
      { id: 'mrp-rec', label: 'Order Recommendation', path: '/mrp/recommendations' },
    ]
  },
  {
    id: 'calendar', label: 'Calendar', children: [
      { id: 'cal-main', label: 'Company Calendar', path: '/mrp/calendar' },
    ]
  },
  {
    id: 'financials', label: 'Financials', children: [
      {
        id: 'fin-aging', label: 'Aging', children: [
          { id: 'fin-ar', label: 'Customer Receivables Aging', path: '/financials/aging/customer-receivables' },
          { id: 'fin-ap', label: 'Vendor Liabilities Aging', path: '/financials/aging/vendor-liabilities' },
        ]
      },
    ]
  },
  {
    id: 'vendor-mgmt', label: 'Vendor Management', children: [
      { id: 'vm-workspace', label: 'Workspace', path: '/vendor-management' },
      { id: 'vm-performance', label: 'Vendor Performance', path: '/vendor-management/performance' },
      { id: 'vm-score', label: 'Vendor Score', path: '/vendor-management/score' },
      { id: 'vm-activity', label: 'Vendor Activity', path: '/vendor-management/activity' },
    ]
  },
];

function hasChildren(item) {
  return Array.isArray(item.children) && item.children.length > 0;
}

function isItemActive(item, currentPath) {
  if (item.path && currentPath === item.path) return true;
  if (item.id === 'vm-workspace' && currentPath === '/vendor-management') return true;
  if (item.id === 'vm-performance') {
    return currentPath === '/vendor-management/performance'
      || currentPath.startsWith('/vendor-management/vendor/');
  }
  if (item.id === 'vm-score' && currentPath.startsWith('/vendor-management/score')) return true;
  if (item.id === 'vm-risk' && currentPath.startsWith('/vendor-management/risk')) return true;
  if (item.id === 'vm-activity' && currentPath.startsWith('/vendor-management/activity')) { return true; }
  if (item.path && item.path !== '/vendor-management' && currentPath.startsWith(`${item.path}/`)) return true;
  if (hasChildren(item)) return item.children.some((c) => isItemActive(c, currentPath));
  return false;
}

function pathMatchesPrefix(hoverPath, prefix) {
  if (hoverPath.length < prefix.length) return false;
  return prefix.every((id, i) => hoverPath[i] === id);
}

function SubmenuArrow({ open }) {
  return (
    <span className={`sidebar-chevron ${open ? 'sidebar-chevron-open' : ''}`}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </span>
  );
}

function CascadeFlyout({ children, title, pos }) {
  const { cancelClose, scheduleClose } = useContext(MenuHoverContext);
  const boxRef = useRef(null);
  const [adjusted, setAdjusted] = useState(null);

  /**
   * Keep the whole menu on screen.
   *
   * The flyout opened level with the row that spawned it and nothing checked whether
   * it would fit below, so a category low in the sidebar - Vendor Management, with six
   * entries - ran off the bottom of the window and the last items could not be reached
   * at all.
   *
   * Its height is only known once it has rendered, which is why this measures rather
   * than estimates: the number of entries differs from one category to the next. If it
   * is taller than the window even when lifted, the CSS lets it scroll.
   */
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const h = el.getBoundingClientRect().height;
    const margin = 8;
    const maxTop = window.innerHeight - h - margin;
    const top = Math.max(margin, Math.min(pos.top, maxTop));
    setAdjusted(top === pos.top ? null : top);
  }, [pos.top, pos.left, children]);

  return createPortal(
    <div
      className="fixed z-[200]"
      style={{
        top: adjusted != null ? adjusted : pos.top,
        left: pos.left - 12,
        paddingLeft: 12,
      }}
      onMouseEnter={cancelClose}
      onMouseLeave={scheduleClose}
    >
      <div ref={boxRef} className="sidebar-flyout" role="menu">
        {title && <div className="sidebar-flyout-header">{title}</div>}
        <div className="space-y-0.5">{children}</div>
      </div>
    </div>,
    document.body
  );
}

function CascadeItem({
  item,
  currentPath,
  onNavigate,
  depth = 0,
  ancestorPath = [],
  hoverPath,
  setHoverPath,
  getBadge,
}) {
  const { cancelClose } = useContext(MenuHoverContext);
  const rowRef = useRef(null);
  const [flyoutPos, setFlyoutPos] = useState({ top: 0, left: 0 });

  const nested = hasChildren(item);
  const myPath = [...ancestorPath, item.id];
  const isOpen = nested && pathMatchesPrefix(hoverPath, myPath);
  const isActive = isItemActive(item, currentPath);
  const isHighlighted = isActive || isOpen;
  const badge = getBadge?.(item);

  const updateFlyoutPos = () => {
    if (rowRef.current) {
      const r = rowRef.current.getBoundingClientRect();
      setFlyoutPos({ top: r.top, left: r.right });
    }
  };

  const handleEnter = () => {
    cancelClose();
    setHoverPath(myPath);
    if (nested) updateFlyoutPos();
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const sync = () => updateFlyoutPos();
    window.addEventListener('scroll', sync, true);
    window.addEventListener('resize', sync);
    return () => {
      window.removeEventListener('scroll', sync, true);
      window.removeEventListener('resize', sync);
    };
  }, [isOpen]);

  const rowCls = [
    'sidebar-row',
    depth === 0 ? 'sidebar-row-root' : 'sidebar-row-leaf',
    isHighlighted ? 'sidebar-row-active' : 'sidebar-row-idle',
    isOpen && !isActive ? 'sidebar-row-open' : '',
    isHighlighted ? 'sidebar-row-accent' : '',
  ].filter(Boolean).join(' ');

  const handleClick = () => {
    if (!nested && item.path) {
      setHoverPath([]);
      onNavigate(item.path);
    }
  };

  const flyout = isOpen && (
    <CascadeFlyout title={depth === 0 ? item.label : null} pos={flyoutPos}>
      {item.children.map((child) => (
        <CascadeItem
          key={child.id}
          item={child}
          currentPath={currentPath}
          onNavigate={onNavigate}
          depth={depth + 1}
          ancestorPath={myPath}
          hoverPath={hoverPath}
          setHoverPath={setHoverPath}
          getBadge={getBadge}
        />
      ))}
    </CascadeFlyout>
  );

  return (
    <div className="relative" onMouseEnter={handleEnter}>
      <button
        type="button"
        role="menuitem"
        ref={rowRef}
        aria-haspopup={nested || undefined}
        aria-expanded={nested ? isOpen : undefined}
        onClick={handleClick}
        className={rowCls}
      >
        <span className="truncate-text flex-1 text-left">{item.label}</span>
        {badge > 0 && <span className="sidebar-badge">+{badge}</span>}
        {nested && <SubmenuArrow open={isOpen} />}
      </button>
      {flyout}
    </div>
  );
}

export default function Sidebar() {
  const { user, hasModuleAccess } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hoverPath, setHoverPath] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const closeTimerRef = useRef(null);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setHoverPath([]);
      closeTimerRef.current = null;
    }, MENU_CLOSE_DELAY);
  }, [cancelClose]);

  useEffect(() => () => cancelClose(), [cancelClose]);

  const fetchUnread = useCallback(() => {
    api.get('/notifications/count').then((d) => setUnreadCount(d.count || 0)).catch(() => { });
  }, []);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    // Marking something read anywhere updates this straight away, rather than leaving
    // a stale number in the sidebar for up to a minute.
    window.addEventListener('mrp:notifications-changed', fetchUnread);
    return () => {
      clearInterval(interval);
      window.removeEventListener('mrp:notifications-changed', fetchUnread);
    };
  }, [fetchUnread]);

  useEffect(() => {
    setHoverPath([]);
  }, [location.pathname]);

  const visibleModules = MODULE_TREE.filter((mod) => {
    if (mod.id === 'vendor-mgmt') {
      return hasModuleAccess('purchasing') || hasModuleAccess('financials');
    }
    return hasModuleAccess(mod.id);
  });
  const isSuperAdmin = user?.role_name === 'Super Admin';

  const adminTree = isSuperAdmin ? [{
    id: 'administration',
    label: 'Administration',
    children: [
      { id: 'admin-panel', label: 'Admin Panel', path: '/admin/panel' },
      { id: 'total-projects', label: 'Total Projects', path: '/projects/total' },
      { id: 'notifications', label: 'Notifications', path: '/admin/notifications' },
      {
        id: 'setup', label: 'Setup', children: [
          {
            id: 'setup-inv', label: 'Inventory', children: [
              { id: 'warehouses', label: 'Warehouses', path: '/admin/setup/warehouses' },
            ]
          },
        ]
      },
    ],
  }] : [];

  const sidebarItems = [...adminTree, ...visibleModules];
  const getBadge = (item) => (item.id === 'notifications' ? unreadCount : null);
  const menuHoverValue = { cancelClose, scheduleClose };

  return (
    <MenuHoverContext.Provider value={menuHoverValue}>
      <aside
        className="sidebar-shell w-56 shrink-0 select-none relative z-30"
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
      >
        <div className="sidebar-nav-label">Navigation</div>
        <nav className="flex-1 overflow-y-auto pb-4 px-2">
          <div className="space-y-1">
            {sidebarItems.map((item) => (
              <CascadeItem
                key={item.id}
                item={item}
                currentPath={location.pathname}
                onNavigate={(path) => navigate(path)}
                hoverPath={hoverPath}
                setHoverPath={setHoverPath}
                getBadge={getBadge}
              />
            ))}
          </div>
        </nav>
      </aside>
    </MenuHoverContext.Provider>
  );
}
